import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";
import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    headerWrapper?: InjectableCss;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            @media (min-width: 1225px) {
                margin-bottom: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 30px;
                        `,
                        css`
                            margin-bottom: 30px;
                        `,
                        css`
                            margin-bottom: 131px;
                            .page-header {
                                position: fixed;
                                top: 0;
                                left: 0;
                                width: 100%;
                                z-index: 1000;
                                background: #fff;
                            }
                        `,
                        css``,
                        css`
                            margin-bottom: 30px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    container: {
        css: css`
            background-color: #000;
            min-height: 70px;
            align-items: center;
            padding: 0 15px;
            display: flex;
            transition: position 0.3s ease-in-out;
            &.fixed {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                z-index: 1000;
            }
            > div:first-child {
                max-width: 300px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                max-width: unset;
                                width: 100%;
                                div[class^="FormFieldStyle"] {
                                    width: 100%;
                                }
                            `,
                    ],
                    "max",
                )}
            }
            > div:last-child {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                height: auto;
                flex-grow: 1;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                max-width: 100px;
                                width: 0;
                            `,
                    ],
                    "max",
                )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    headerWrapper: {
        css: css`
            max-width: 1280px !important;
            padding: 0 !important;
            & + div[class^="PageStyle"] {
                padding: 0px;
                nav[data-test-selector="pageBreadcrumbs"] {
                    width: 100%;
                    display: flex;
                    max-width: 1225px;
                    margin: 0 auto;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                    css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                ])}
                    @media only screen and (max-width: 1225px) {
                        padding-left: 10px !important;
                        padding-right: 10px !important;
                    }
                    > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        font-size: 14px;
                    }
                    span[aria-current="page"] {
                        color: ${getColor("text.accent")};
                        font-weight: bold;
                        font-size: 14px;
                        text-transform: capitalize;
                        opacity: 1;
                    }

                    > a > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        text-transform: capitalize;
                        opacity: 1;
                        font-size: 14px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                                padding: 0;
                            `,
                    ],
                    "max",
                )}
            }
            ~ main[class^="PageStyle"] {
                min-height: 500px;
                max-width: 1280px;
                padding: 0;
                overflow: hidden;
                background-color: #f6f6f6;  
                > div[class^="LoadingOverlayStyle"]:first-of-type {
                    > div:last-child {
                        > div[class^="GridWrapper"] {
                            max-width: 1280px;
                        }
                    }
                }
                main {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                }

                > div > div[class^="GridWrapper"] {
                    max-width: 1280px;
                    padding: 15px;
                }

                div[class^="GridWrapper"]{
                    max-width: 1280px;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                padding: 0;
                                padding-top: 0px;
                            `,
                        css`
                                padding: 0;
                                padding-top: 0px;
                            `,
                        null,
                        null,
                    ],
                    "max",
                )}
            }
            ~ main[data-test-selector="homePage"] {
                padding: 0;
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const pageHeaderRef = useRef<HTMLDivElement>(null);

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (headerRef.current) {
                const isDesktop = window.innerWidth >= 1024; // Adjust breakpoint as needed
                if (isDesktop && window.scrollY > headerRef.current.offsetTop) {
                    headerRef.current.classList.add("fixed");
                } else {
                    headerRef.current.classList.remove("fixed");
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    return (
        <Page as="header" isHeader={true} {...styles.page} {...styles.headerWrapper}>
            <StyledWrapper className="page-header" ref={pageHeaderRef}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="SecondaryNavigation" fixed />
                    <StyledWrapper ref={headerRef} {...styles.container}>
                        <Zone contentId={id} zoneName="Search" />
                        <Zone contentId={id} zoneName="MainNavigation" fixed />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </StyledWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;